import { Link, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';

const spring = { type: 'spring', stiffness: 380, damping: 28 } as const;

export default function NotFound() {
  const { t } = useTranslation();
  const location = useLocation();
  const path = location.pathname;

  return (
    <div className="relative min-h-[calc(100dvh-12rem)] overflow-hidden bg-[#fafbfc]">
      <SEO title={t('notFound.seo.title')} noIndex />

      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_90%_60%_at_50%_-10%,rgba(0,102,255,0.14),transparent_55%)]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(15,23,42,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.04)_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:radial-gradient(ellipse_70%_55%_at_50%_35%,black,transparent)]"
        aria-hidden
      />

      <svg
        className="pointer-events-none absolute left-1/2 top-[8%] h-[min(42vh,320px)] w-[min(96vw,720px)] -translate-x-1/2 text-[#1769ff]/25 md:top-[10%]"
        viewBox="0 0 720 280"
        fill="none"
        aria-hidden
      >
        <path
          d="M40 220 C140 40 280 260 400 120 S620 200 700 20"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeDasharray="10 14"
          strokeLinecap="round"
        />
        <circle cx="40" cy="220" r="6" fill="#1769ff" fillOpacity="0.35" />
        <circle cx="700" cy="20" r="8" stroke="#1769ff" strokeWidth="2" fill="none" strokeOpacity="0.45" />
      </svg>

      <div className="relative z-[1] mx-auto flex max-w-3xl flex-col items-center px-4 pb-20 pt-16 text-center md:pb-28 md:pt-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#1769ff]/15 bg-white/80 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#1769ff] shadow-sm shadow-blue-500/5 backdrop-blur-sm"
        >
          <i className="ri-error-warning-line text-base" aria-hidden />
          {t('notFound.badge')}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ ...spring, delay: 0.05 }}
          className="relative"
        >
          <span className="select-none bg-gradient-to-br from-[#0b84ff] via-[#1769ff] to-[#042f7e] bg-clip-text text-[clamp(5.5rem,22vw,10rem)] font-black leading-none tracking-tighter text-transparent drop-shadow-sm">
            404
          </span>
          <motion.span
            className="absolute -right-1 -top-1 flex h-11 w-11 items-center justify-center rounded-xl bg-neutral-900 text-lg text-white shadow-lg shadow-neutral-900/30 md:h-12 md:w-12 md:text-xl"
            initial={{ rotate: -12, scale: 0 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{ ...spring, delay: 0.2 }}
            aria-hidden
          >
            <i className="ri-truck-line" />
          </motion.span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...spring, delay: 0.12 }}
          className="mt-6 max-w-xl text-balance text-2xl font-bold tracking-tight text-neutral-900 md:text-3xl"
        >
          {t('notFound.title')}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...spring, delay: 0.18 }}
          className="mt-4 max-w-lg text-pretty text-base leading-relaxed text-neutral-600 md:text-lg"
        >
          {t('notFound.description')}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...spring, delay: 0.24 }}
          className="mt-5 w-full max-w-lg rounded-2xl border border-neutral-200/80 bg-white/90 px-4 py-3 text-left shadow-sm shadow-neutral-900/[0.04] backdrop-blur-sm"
        >
          <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">{t('notFound.requestedPathLabel')}</p>
          <p className="mt-1 break-all font-mono text-sm text-neutral-800">{path}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...spring, delay: 0.3 }}
          className="mt-10 flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:justify-center"
        >
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#1769ff] px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:bg-[#0f55d4] active:scale-[0.99]"
          >
            <i className="ri-home-4-line text-lg" aria-hidden />
            {t('notFound.backHome')}
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-neutral-900 bg-white px-6 py-3.5 text-sm font-semibold text-neutral-900 transition hover:bg-neutral-50 active:scale-[0.99]"
          >
            <i className="ri-customer-service-2-line text-lg" aria-hidden />
            {t('notFound.contactUs')}
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-neutral-200 bg-white px-6 py-3.5 text-sm font-semibold text-neutral-700 transition hover:border-neutral-300 hover:bg-neutral-50 active:scale-[0.99]"
          >
            <i className="ri-price-tag-3-line text-lg" aria-hidden />
            {t('notFound.pricing')}
          </Link>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45, duration: 0.4 }}
          className="mt-12 text-xs text-neutral-400"
        >
          {t('notFound.tagline')}
        </motion.p>
      </div>
    </div>
  );
}
